import { range } from "lodash";
import PropTypes, { Validator } from "prop-types";
import { LayoutPropsType } from "../typings/block-prop-types";

/**
 * Type definition for how LayoutOptions are constructed.
 * Basically, layout options are either a list of booleans, strings or numbers.
 * This is because layoutOptions are restricted to a list of "options" for the client to select from.
 */
type LayoutPropsOptionType = [true, false] | string[] | number[];
export type LayoutPropsOptions = {
  [key: string]: LayoutPropsOptionType;
};

/**
 * Helper type declaration for the `processLayoutOptions` return type.
 * This can be used for example to convert a string[] to string as type
 */
type ArrayElement<ArrayType extends LayoutPropsOptionType> =
  ArrayType extends readonly (infer ElementType)[] ? ElementType : never;

/**
 * Helper type declaration for the `processLayoutOptions` return type.
 * This type declares that the given LayoutPropsOptions (which is a key-value, of which the value is a list of options)
 * will be converted to PropTypes validator value like:
 *
 * {
 *     SomeOption: ["Yes", "No", "Maybe"]
 * }
 *
 * Will be:
 *
 * {
 *     SomeOption: PropTypes.oneOf(["yes", "no", "Maybe"])
 * }
 *
 * Which can be interpreted like:
 *
 * {
 *     SomeOption: string (or: "yes"|"no"|"Maybe")
 * }
 */
type LayoutProps<T extends LayoutPropsOptions> = {
  [K in keyof T]: Validator<ArrayElement<T[K]>>;
};

/**
 * This function uses generics to dynamically create LayoutProps from LayoutPropsOptions.
 *
 * Let's say there is a layoutProp that is stated as:
 * {
 *     backgroundColor: string
 * }
 *
 * As you can see, the prop ITSELF is a string. However, the user (within storybook) is only eligible to choose from
 * a particular subset of strings (enum). To do this, the layoutProps are defined like:
 * {
 *     backgroundColor: enum("blue", "green", "purple")
 * }
 *
 * This function (using generics) will process these props as a PropType.oneOf the given values.
 * The output is a typed object declaration of which the input propTypes keys must also exist within the output object.
 * The value is of each key within the output is converted to a propTypes.oneOf validator that can be used by react.
 *
 * Then, to create a type from these propTypes, simply use: `type LayoutProps = PropTypes.InferProps<typeof layoutPropsType>;`
 *
 *
 *
 * ----- FULL EXAMPLE -----
 *
 * const layoutOptions = {
 *     SomeBool: LayoutOptions.Boolean,
 *     SomeInt: Range(1,10000),
 *     SomeString: ["hello", "friend"],
 *     SomeEnum: LayoutOptions.Options(MyEnum)
 * }
 *
 * const layoutProps = processLayoutProps(layoutOptions)
 * export type LayoutProps = PropTypes.InferProps<typeof layoutProps>;
 *
 * -----
 *
 * The LayoutProps (type) can be used as a type within React components, of which the input layoutProps (const)
 * can be used to present to the user to choose a particular value from.
 *
 * @param propTypes
 */
export const processLayoutOptions = <T extends LayoutPropsOptions>(
  propTypes: T
): LayoutProps<T> => {
  const output: { [key: string]: Validator<any> } = {};
  Object.keys(propTypes).forEach((prop) => {
    if (prop in propTypes) {
      output[prop] = PropTypes.oneOf(propTypes[prop] as []);
    }
  });
  return output as LayoutProps<T>;
};

/**
 * Little helper function to create LayoutProp options
 * Can be used like:
 *
 * enum MyEnum = {someEnumValue, anotherEnumValue}
 *
 * const LayoutProps = {
 *     SomeBool: LayoutOptions.Boolean,
 *     SomeInt: Range(1,10000),
 *     SomeString: ["hello", "friend"],
 *     SomeEnum: LayoutOptions.Options(MyEnum)
 * }
 */
const BooleanOption: [true, false] = [true, false];
export const LayoutOptions = {
  Boolean: BooleanOption,
  Range: (start: number, end: number, step?: number): number[] =>
    range(start, end, step),
  Numbers: (numbers: number[]): number[] => numbers,
  List: (items: LayoutPropsOptionType): LayoutPropsOptionType => items,
  Options: (options: LayoutPropsType): LayoutPropsOptionType =>
    Object.values(options) as LayoutPropsOptionType,
};
